import { Check, EyeOff, Landmark } from "lucide-react";
import { shortDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SectionTitle } from "@/components/shared/ui";
import { FixBox } from "@/components/applicant/stageUi";
import { FlightPath } from "@/components/applicant/FlightPath";

const OUTCOME = { accepted: "Accepted. The agent will be in touch about next steps.", declined: "The agent has decided not to go ahead this time." };
const CHECKED = [
  "Each document is the right type and from the right months",
  "Files are originals, not screenshots or edited copies",
  "Payslip numbers add up: pay before tax, minus deductions, equals take-home pay",
  "Names match across your documents, and ID is in date",
];

const Tracker = ({ steps }) => (
  <ol className="mt-8" data-testid="status-tracker">
    {steps.map((s, i) => {
      const current = !s.done && (i === 0 || steps[i - 1].done);
      return (
        <li key={s.key} className="relative flex gap-3 pb-6 last:pb-0" data-testid={`tracker-${s.key}`} data-done={s.done ? "true" : "false"}>
          {i < steps.length - 1 && <span className={cn("absolute left-[11px] top-6 h-[calc(100%-24px)] w-0.5", s.done ? "bg-success" : "bg-border")} />}
          <span className={cn("relative grid size-6 shrink-0 place-items-center rounded-full",
            s.done ? "bg-success text-white" : current ? "border-2 border-foreground bg-background" : "border-2 border-border bg-background")}>
            {s.done && <Check className="size-3.5" strokeWidth={3} />}
          </span>
          <div className="pt-0.5">
            <p className={cn("text-base", s.done || current ? "font-semibold" : "text-muted-foreground")}>{s.label}</p>
            {s.at && <p className="text-xs text-muted-foreground">{shortDate(s.at)}</p>}
            {current && <p className="text-xs text-muted-foreground">{s.key === "in_review" ? "Waiting for the agent to open it" : "Next"}</p>}
          </div>
        </li>
      );
    })}
  </ol>
);

/** Stage 6: submitted, with a Submitted → In review → Decision tracker. */
export const DoneStage = ({ data }) => {
  const steps = data.submission.tracker;
  const decision = steps.find((s) => s.key === "decision");
  return (
    <div className="rise" data-testid="stage-done">
      <FlightPath landed stops={[0.25, 0.5, 0.75]} testid="done-flight-path" />
      <p className="mt-6 text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">Sent to {data.let.agency_name}</p>
      <h1 className="mt-2 text-[30px] font-semibold leading-[1.1] tracking-tight" data-testid="done-title">You're nearly home</h1>
      <p className="mt-3 text-base leading-relaxed text-muted-foreground">Thank you. {data.let.agency_name} can now review your application for {data.let.address}. They usually decide within a day. Come back to this link to see where it's got to.</p>

      <Tracker steps={steps} />
      {decision?.outcome && <p className="mt-4 text-base font-medium" data-testid="done-decision">{OUTCOME[decision.outcome]}</p>}

      {data.submission.open_requests.length > 0 && (
        <div className="mt-6 flex flex-col gap-2" data-testid="done-open-requests">
          <SectionTitle>The agent asked for something</SectionTitle>
          {data.submission.open_requests.map((r) => (
            <FixBox key={r.id} title={r.step_label}>{r.message} <span className="block pt-1 text-xs text-muted-foreground">Open the link in the email we sent to {data.submission.email} to fix it.</span></FixBox>
          ))}
        </div>
      )}

      <div className="mt-10 rounded-2xl border bg-background p-5" data-testid="done-what-checked">
        <p className="text-sm font-semibold">What was checked</p>
        <ul className="mt-3 flex flex-col gap-2 text-sm">
          {CHECKED.map((line) => <li key={line} className="flex gap-2"><Check className="mt-0.5 size-4 shrink-0 text-success" strokeWidth={2.5} />{line}</li>)}
        </ul>
        {data.checklist.length > 0 && (
          <ul className="mt-3 flex flex-col gap-1 border-t pt-3 text-xs text-muted-foreground">
            {data.checklist.map((i) => <li key={i.key}>✓ {i.title}: {i.detail}</li>)}
          </ul>
        )}
      </div>
      <div className="mt-3 rounded-2xl bg-background/60 p-5 ring-1 ring-border" data-testid="done-who-sees">
        <p className="flex items-center gap-1.5 text-sm font-semibold"><EyeOff className="size-4" /> Who sees what</p>
        <p className="mt-2 text-sm">The agent first sees a <span className="font-medium">blind summary</span>: whether you meet the criteria, with no name, photo, age, nationality, household or income source.</p>
        <p className="mt-2 flex gap-2 text-sm"><Landmark className="mt-0.5 size-4 shrink-0" />Later, they only see the transactions that prove your income. Never your spending.</p>
      </div>
    </div>
  );
};
